import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useFormFields } from 'helpers';
import {
  Alerts,
  Container,
  PageContent,
  Pagination,
  Table,
  FiltersControls,
  Spinner,
  FiltersCustom,
} from 'components';
import {
  loadMaPeopleYcCreator,
  setMaPeopleYcSortCreator,
  setMaPeopleYcPageCreator,
  setMaPeopleYcPerPageCreator,
  downloadMaPeopleReportCreator,
  setMaPeopleYcFiltersCreator,
} from 'store/actionsCreators';
import { ReactSVG } from 'react-svg';
import { iconList } from 'assets';
import { Margin } from 'styled-components-spacing';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Content, Wrapper, Icon, RowStyled } from '../List/List.styled';

function Members({
  items,
  page,
  pages,
  perPage,
  sort,
  loadUsers,
  setSort,
  loading,
  setPage,
  setPerPage,
  downloadReport,
  filters,
  setFilters,
  localMemberAssociation,
}) {
  const { t } = useTranslation();
  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    createdFrom: null,
    certified: null,
    membership: null,
  });

  const simpleOptions = [
    {
      value: 'no',
      label: 'No',
    },
    {
      value: 'yes',
      label: 'Yes',
    },
  ];

  const filtersProps = {
    firstName: {
      type: 'input',
      name: 'first-name',
      id: 'f-name',
      label: t('page.ma.peopleAdd.firstName'),
      placeholder: t('forms.firstNamePlaceholder'),
    },
    lastName: {
      type: 'input',
      name: 'last-name',
      id: 'f-lastName',
      label: t('page.ma.peopleAdd.lastName'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    email: {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.ma.peopleAdd.email'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    createdFrom: {
      type: 'datepicker',
      name: 'createdFrom',
      id: 'f-createdFrom',
      label: t('page.ma.peopleAdd.createdFrom'),
      placeholder: t('forms.createdFromPlaceholder'),
    },
    certified: {
      type: 'select',
      name: 'certified',
      id: 'f-jobTitle',
      label: t('page.ma.peopleAdd.certified'),
      placeholder: t('forms.certifiedPlaceholder'),
      options: simpleOptions,
    },
    membership: {
      type: 'select',
      name: 'membership',
      id: 'f-membership',
      label: t('page.ma.peopleAdd.membership'),
      placeholder: t('forms.membershipPlaceholder'),
      options: simpleOptions,
    },
  };

  useEffect(() => {
    setPage(1);
  }, [perPage]);

  useEffect(() => {
    loadUsers();
  }, [page, perPage, sort]);

  useEffect(() => {
    setPage(1);
    loadUsers();
  }, [filters]);

  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = sort || [];
    const sortByNewColumn = currentSortName !== name;

    if (sortByNewColumn) {
      setSort([name, 'desc']);
      return;
    }

    const newSortStatus = currentSortStatus === 'desc' ? 'asc' : 'desc';
    setSort([name, newSortStatus]);
  };

  const handleChangePerPage = ({ value }) => {
    setPerPage(value);
  };

  const isCurrentMa = (user) => {
    return (
      user.memberAssociationPeople &&
      user.memberAssociationPeople.memberAssociation.id === localMemberAssociation
    );
  };

  const getUserStatus = (user) => {
    if (!user.memberAssociationPeople || !isCurrentMa(user)) return 'No';
    return user.memberAssociationPeople.status;
  };

  const getYoungCrewStatus = (user) => {
    return user.yCPeople && user.yCPeople.status === 'Yes' ? 'Yes' : 'No';
  };

  const downloadButton = () => (
    <Wrapper onClick={() => downloadReport()}>
      <Icon>
        <ReactSVG src={iconList.download} />
      </Icon>
      <Content>
        <p className="word-break">{t('page.ma.peopleMembers.reportTitleYc')}</p>
        <Margin top="4">
          <div className="link uppercase">{t('page.ma.peopleList.downloadReport')}</div>
        </Margin>
      </Content>
    </Wrapper>
  );

  return (
    <>
      <Container>
        <PageContent>
          <Alerts type="ma" />
          <p dangerouslySetInnerHTML={{ __html: t('page.ma.peopleMembers.info') }} />
          <Margin
            as="p"
            top={{ xs: 16 }}
            dangerouslySetInnerHTML={{ __html: t('page.ma.peopleMembers.info2') }}
          />
          <Margin top={{ xs: 16 }}>
            <FiltersCustom
              title={t('page.ma.peopleAdd.filterTitle')}
              filters={filtersProps}
              filterFields={filterFields}
              handleFilterFieldChange={handleFilterFieldChange}
              onFiltersApply={setFilters}
            />
          </Margin>
          <Margin top={{ xs: 16 }}>
            <RowStyled>
              {downloadButton()}
              <FiltersControls perPageOnChange={handleChangePerPage} perPageSelected={perPage} />
            </RowStyled>
          </Margin>
          <Table tablebreakpoint="lg">
            <Table.Head>
              <Table.Row>
                <Table.Heading sort={handleSortBy('firstLastName')}>
                  {t('page.ma.peopleMembers.firstAndLastName')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('email')}>
                  {t('page.ma.peopleMembers.email')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('createdAt')}>
                  {t('page.ma.peopleList.createdAt')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('role')}>
                  {t('page.ma.peopleMembers.certified')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('date')}>
                  {t('page.ma.peopleMembers.membershipStatus')}
                </Table.Heading>
                <Table.Heading>{t('page.ma.peopleList.youngCrewMember')}</Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !items.length ? (
                <Table.Row>
                  <Table.Cell colSpan={4}>No results found</Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && items.length
                ? items.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.firstAndLastName')}:</Table.Label>
                        <Link to={`/user-profile/${user.uniqueUrl}`}>
                          {`${user.firstName} ${user.lastName}`}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.email')}:</Table.Label>
                        {user.email}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.createdAt')}:</Table.Label>
                        {user.createdAt && moment(user.createdAt).format('DD/MM/YYYY')}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.certified')}:</Table.Label>
                        {user.certificates.length ? 'Yes' : 'No'}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.membershipStatus')}:</Table.Label>
                        {getUserStatus(user)}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.youngCrewMember')}:</Table.Label>
                        {getYoungCrewStatus(user)}
                      </Table.Cell>
                    </Table.Row>
                  ))
                : null}
            </Table.Body>
          </Table>
          <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
        </PageContent>
      </Container>
    </>
  );
}

Members.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loadUsers: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  sort: PropTypes.arrayOf([PropTypes.string, PropTypes.string]),
  downloadReport: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
  localMemberAssociation: PropTypes.number.isRequired,
};

Members.defaultProps = {
  sort: [],
};

const mapStateToProps = ({ ma, loading }) => ({
  ...ma.people.yc,
  loading: !!loading.filter((item) => item.type === 'MA').length,
});

const mapDispatchToProps = (dispatch) => ({
  loadUsers: () => dispatch(loadMaPeopleYcCreator()),
  setSort: (payload) => dispatch(setMaPeopleYcSortCreator(payload)),
  setPage: (payload) => dispatch(setMaPeopleYcPageCreator(payload)),
  setPerPage: (payload) => dispatch(setMaPeopleYcPerPageCreator(payload)),
  downloadReport: () => dispatch(downloadMaPeopleReportCreator('youngCrew')),
  setFilters: (payload) => dispatch(setMaPeopleYcFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Members);
